import React, { useState } from 'react';
import { voucherService, ValidateVoucherResponse, formatCurrency } from '../../services/voucherService';
import './VoucherCodeInput.css';

export interface ValidatedVoucher {
  code: string;
  remainingAmount: number;
  amountToApply: number;
  validUntil?: string;
  // T-006: Fahrtbezug (informativ)
  tourReferenceText?: string | null;
}

interface VoucherCodeInputProps {
  onVoucherApplied: (voucher: ValidatedVoucher) => void;
  onVoucherRemoved: () => void;
  bookingTotal: number;
  disabled?: boolean;
}

const VoucherCodeInput: React.FC<VoucherCodeInputProps> = ({
  onVoucherApplied,
  onVoucherRemoved,
  bookingTotal,
  disabled = false,
}) => {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appliedVoucher, setAppliedVoucher] = useState<ValidatedVoucher | null>(null);
  const [validationResult, setValidationResult] = useState<ValidateVoucherResponse | null>(null);

  const handleValidate = async () => {
    if (!code.trim()) {
      setError('Bitte geben Sie einen Gutschein-Code ein');
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await voucherService.validateVoucher(code.trim());
      setValidationResult(result);

      if (!result.isValid) {
        setError(result.message);
        return;
      }

      // Calculate amount to apply (min of remaining amount and booking total)
      const amountToApply = Math.min(result.remainingAmount || 0, bookingTotal);

      if (amountToApply <= 0) {
        setError('Der Gutschein hat keinen anwendbaren Wert');
        return;
      }

      const voucher: ValidatedVoucher = {
        code: result.code || code.trim().toUpperCase(),
        remainingAmount: result.remainingAmount || 0,
        amountToApply,
        validUntil: result.validUntil,
        tourReferenceText: result.tourReferenceText ?? null,
      };

      setAppliedVoucher(voucher);
      onVoucherApplied(voucher);
    } catch (err: any) {
      setError(err.message || 'Fehler bei der Gutschein-Validierung');
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemove = () => {
    setAppliedVoucher(null);
    setValidationResult(null);
    setCode('');
    setError(null);
    onVoucherRemoved();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !disabled && !appliedVoucher) {
      e.preventDefault();
      handleValidate();
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  return (
    <div className="voucher-code-input">
      {!appliedVoucher ? (
        <div className="voucher-form">
          <div className="voucher-input-row">
            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              placeholder="GS-2025-XXXX"
              disabled={disabled || isValidating}
              className={`voucher-input ${error ? 'error' : ''}`}
              maxLength={20}
            />
            <button
              type="button"
              onClick={handleValidate}
              disabled={disabled || isValidating || !code.trim()}
              className="voucher-validate-btn"
            >
              {isValidating ? 'Prüfe...' : 'Prüfen'}
            </button>
          </div>

          {error && (
            <div className="voucher-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
        </div>
      ) : (
        <div className="voucher-applied">
          <div className="voucher-success-badge">
            <span className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="12" fill="#16a34a"/>
                <path d="M7 12.5L10.5 16L17 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span>Gutschein angewendet</span>
          </div>

          <div className="voucher-details">
            <div className="voucher-detail-row">
              <span className="label">Code:</span>
              <span className="value code">{appliedVoucher.code}</span>
            </div>
            <div className="voucher-detail-row">
              <span className="label">Gutscheinwert:</span>
              <span className="value">{formatCurrency(appliedVoucher.remainingAmount)}</span>
            </div>
            {appliedVoucher.tourReferenceText && (
              <div className="voucher-detail-row">
                <span className="label">Gekauft für:</span>
                <span className="value">{appliedVoucher.tourReferenceText}</span>
              </div>
            )}
            <div className="voucher-detail-row highlight">
              <span className="label">Abzug:</span>
              <span className="value discount">-{formatCurrency(appliedVoucher.amountToApply)}</span>
            </div>
            {appliedVoucher.validUntil && (
              <div className="voucher-detail-row">
                <span className="label">Gültig bis:</span>
                <span className="value">{formatDate(appliedVoucher.validUntil)}</span>
              </div>
            )}
          </div>

          {/* T-035 (Anlage 2.4 Punkt 3): keine Teileinlösung, Restwert verfällt */}
          {appliedVoucher.amountToApply < appliedVoucher.remainingAmount && (
            <div className="voucher-info">
              <span className="info-icon">ℹ️</span>
              <span>
                Der Gutschein wird mit dieser Buchung vollständig eingelöst. Der nicht verbrauchte Restwert von{' '}
                <strong>{formatCurrency(appliedVoucher.remainingAmount - appliedVoucher.amountToApply)}</strong> verfällt.
              </span>
            </div>
          )}

          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled}
            className="voucher-remove-btn"
          >
            Gutschein entfernen
          </button>
        </div>
      )}
    </div>
  );
};

export default VoucherCodeInput;
